import React, { useState, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import { Table, Select, Typography, Alert, Flex } from "antd"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"
import { Icons } from "assets/icons"
import { spacings } from "themes/variables"
import { Text } from "ui/Typography"
import ExternalLink from "ui/Icon/DSIcon/ExternalLink"
import { generateBookingLink, generateTripLink } from "./utils"

// Renders bookings requiring replacement + assigned experiences list.
// Determines if action button should be disabled and reports via onValidityChange.
const PartiallySubstitutable = ({ upcomingBookings = [], assignedExperiences = [], warning, onValidityChange, onSelectionsChange, expMessage, bookingMessage }) => {
  const { t } = useTranslation()
  // Track selected replacements keyed by booking id
  const [replacements, setReplacements] = useState(() => {
    const init = {}
    for (const b of upcomingBookings) {
      if (b.replaceWith) init[b.booking_id] = b.replaceWith
    }
    return init
  })

  // Separate bookings into two groups first (before using them in other useMemos)
  const bookingsWithSubstitutes = useMemo(() => {
    return upcomingBookings.filter((b) => {
      const substitutes = b.available_substitutes || []
      return substitutes.length > 0
    })
  }, [upcomingBookings])

  const bookingsWithoutSubstitutes = useMemo(() => {
    return upcomingBookings.filter((b) => {
      const substitutes = b.available_substitutes || []
      return substitutes.length === 0
    })
  }, [upcomingBookings])

  // A booking is satisfied if it has a selected replacement OR is flagged cancellable/rescheduled OR has an explicit error (which blocks overall validity)
  const hasBlockingError = useMemo(() => {
    return upcomingBookings.some((b) => b.errorMessage)
  }, [upcomingBookings])

  // Check if any booking has no available options
  const hasNoOptions = useMemo(() => {
    return bookingsWithoutSubstitutes.length > 0
  }, [bookingsWithoutSubstitutes])

  const allSatisfied = useMemo(() => {
    if (hasBlockingError) return false
    if (hasNoOptions) return false
    // Only check bookings with substitutes available - they all need to have a replacement selected
    return bookingsWithSubstitutes.every((b) => replacements[b.booking_id])
  }, [bookingsWithSubstitutes, replacements, hasBlockingError, hasNoOptions])

  // Notify parent of validity state
  useEffect(() => {
    onValidityChange?.(allSatisfied)
  }, [allSatisfied, onValidityChange])

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionsChange?.(replacements)
  }, [replacements, onSelectionsChange])

  const columns = [
    {
      title: t("equipment.modals.table.experience_name"),
      key: "experienceName",
      render: (_, record) => {
        if (record.__isErrorRow) {
          // Render the error row spanning all columns
          return {
            children: (
              <Alert
                type="error"
                showIcon
                message={
                  <span>
                    {record.errorMessage}{" "}
                    {record.onRefresh && (
                      <Typography.Link className={styles.refreshLink} onClick={() => record.onRefresh?.()}>
                        Refresh
                      </Typography.Link>
                    )}
                  </span>
                }
                className={styles.errorRow}
              />
            ),
            props: { colSpan: 6 }
          }
        }
        const experienceName = record.experience_name || record.experienceName
        const bookingLink = generateBookingLink(record)

        return (
          <Flex gap={spacings.XS} align="center">
            <Typography.Link href={bookingLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
              {experienceName}
              <ExternalLink className="ml-xxs" />
            </Typography.Link>

          </Flex>
        )
      }
    },
    {
      title: t("equipment.modals.table.date"),
      key: "date",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        return record.booked_date
      }
    },
    {
      title: t("equipment.modals.table.time"),
      key: "time",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        if (record.start_time && record.end_time) {
          return `${record.start_time} - ${record.end_time}`
        }
        return record.time || "-"
      }
    },
    {
      title: t("equipment.modals.table.id"),
      key: "id",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        return record.booking_id
      }
    },
    {
      title: t("equipment.modals.table.client_name"),
      key: "clientName",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        return record.client_name || record.clientName
      }
    },
    {
      title: t("equipment.modals.table.replace_with"),
      key: "replaceWith",
      render: (_, record) => {
        if (record.__isErrorRow) return { children: null, props: { colSpan: 0 } }
        const substitutes = record.available_substitutes || []
        const options = substitutes.map((s) => {
          if (typeof s === "object") {
            // For equipment: boat_name + available_assets
            // For assets: boat_name + asset_number (or just boat_name)
            let displayName
            if (s.asset_number) {
              displayName = `Asset #${s.asset_number} (${s.boat_name})`
            } else if (s.available_assets) {
              displayName = `${s.boat_name} (${t("equipment.modals.available")}: ${s.available_assets})`
            } else {
              displayName = s.boat_name || s.name
            }

            return {
              label: displayName,
              value: s.boat_asset_id || s.boat_id || s.id
            }
          }
          return { label: s, value: s }
        })
        const recordId = record.booking_id
        return (
          <Select
            className={styles.replaceSelect}
            placeholder={t("equipment.modals.select_replacement")}
            value={replacements[recordId] || null}
            options={options}
            onChange={(val) => setReplacements((prev) => ({ ...prev, [recordId]: val }))}
            disabled={record.errorMessage || !options.length}
          />
        )
      }
    }
  ]

  // Create table data for bookings WITH substitutes
  const tableDataWithSubstitutes = bookingsWithSubstitutes.map((b) => ({
    ...b,
    key: b.booking_id
  }))

  // Create table data for bookings WITHOUT substitutes
  const tableDataWithoutSubstitutes = bookingsWithoutSubstitutes.map((b) => ({
    ...b,
    key: b.booking_id
  }))

  return (
    <>
      {upcomingBookings.length > 0 && (
        <div>
          <div className={styles.sectionHeader}>
            {t("equipment.modals.assigned_to_bookings")} ({upcomingBookings.length})
          </div>
          <div className={styles.sectionContent}>
            {/* First: Show bookings with available substitutes */}
            {tableDataWithSubstitutes.length > 0 && (
              <Table
                className={styles.upcomingBookingsTable}
                dataSource={tableDataWithSubstitutes}
                pagination={false}
                bordered={false}
                columns={columns}
              />
            )}

            {/* Second: Show warning message if there are bookings without substitutes */}
            {bookingsWithoutSubstitutes.length > 0 && (bookingMessage || warning) && (
              <Flex align="center" gap={spacings.XS} className={styles.errorAlert}>
                <Icons.RedCrossWithCircle />
                <Text>{bookingMessage || warning}</Text>
              </Flex>
            )}

            {/* Third: Show bookings without available substitutes */}
            {tableDataWithoutSubstitutes.length > 0 && (
              <Table
                dataSource={tableDataWithoutSubstitutes}
                pagination={false}
                bordered={false}
                columns={columns}
                showHeader={tableDataWithSubstitutes?.length === 0}
                style={{ marginTop: tableDataWithSubstitutes?.length > 0 || warning ? 16 : 0 }}
              />
            )}
          </div>
        </div>
      )}
      {assignedExperiences.length > 0 && (
        <div className={`${styles.mt16}`}>
          <div className={`${styles.sectionHeader} ${styles.assignedExperiencesHeader}`}>
            {t("equipment.modals.assigned_experiences")} ({assignedExperiences.length})
          </div>
          <div className={styles.sectionContent}>
            {/* Show experience warning message if provided */}
            {expMessage && (
              <Flex align="center" gap={spacings.XS} className={styles.warningAlert}>
                <Icons.OrangeAlertTriangle />
                <Text>{expMessage}</Text>
              </Flex>
            )}
            <Table
              className={styles.assignedExperiencesTable}
              dataSource={assignedExperiences}
              columns={[
                {
                  title: "",
                  key: "name",
                  render: (_, record) => {
                    const { trip_name, name, trip_id, status } = record || {}
                    const tripName = trip_name || name
                    const tripLink = generateTripLink({ trip_id, status })

                    return (
                      <Flex gap={spacings.XS} align="center">
                        <Typography.Link href={tripLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                          {tripName}
                          <ExternalLink className="ml-xxs" />
                        </Typography.Link>

                      </Flex>
                    )
                  }
                }
              ]}
              pagination={false}
              showHeader={false}
              rowClassName={() => styles.experienceTableRow}
            />
          </div>
        </div>
      )}
    </>
  )
}

PartiallySubstitutable.propTypes = {
  upcomingBookings: PropTypes.arrayOf(
    PropTypes.shape({
      experienceName: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      clientName: PropTypes.string.isRequired,
      replaceWith: PropTypes.string,
      replacementOptions: PropTypes.arrayOf(PropTypes.string),
      errorMessage: PropTypes.string,
      onRefresh: PropTypes.func,
      link: PropTypes.string
    })
  ),
  assignedExperiences: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      link: PropTypes.string
    })
  ),
  warning: PropTypes.string,
  onValidityChange: PropTypes.func,
  onSelectionsChange: PropTypes.func,
  expMessage: PropTypes.string,
  bookingMessage: PropTypes.string
}

export default PartiallySubstitutable
